/* eslint-disable max-lines */
import { inject, Injectable } from '@angular/core';
import { toOptimisticMedia } from '@helpers/to-optimistic-media';
import { TranslocoService } from '@jsverse/transloco';
import { WA_WINDOW } from '@ng-web-apis/common';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { formatPatientName, LicenseState, MediaType } from '@pixacare/domain';
import { ActionStatus } from '@pixacare/shared';
import { VideoUploadEvent } from '@services/http/models/video-upload-event';
import { VideoUploadItem } from '@services/http/models/video-upload-item';
import { SequenceHttpService } from '@services/http/sequence.http.service';
import { VideoUploadHttpService } from '@services/http/video-upload.http.service';
import { VideoUploadFileService } from '@services/video-upload/video-upload-file.service';
import { MediaLocalPreviewService } from '@services/media-local-preview/media-local-preview.service';
import {
  catchError,
  concatMap,
  distinctUntilChanged,
  filter,
  from,
  map,
  mergeMap,
  Observable,
  of,
  startWith,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { selectActiveClientCode } from '../active-client/active-client.selectors';
import { clientsActions } from '../clients/clients.actions';
import { licensesActions } from '../licenses/licenses.actions';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { sequencesActions } from '../sequences/sequences.actions';
import { groupSessionsBySequence } from './utils/group-sessions-by-sequence';
import { videoUploadsActions } from './video-uploads.actions';
import {
  selectHasActiveVideoUploads,
  selectSequenceHasWaitingOrInProgressVideos,
  selectVideoUploadEntry,
  selectVideoUploadsState,
} from './video-uploads.selectors';
import { VideoUploadsState } from './video-uploads.state';

@Injectable()
export class VideoUploadsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly videoUploadService = inject(VideoUploadHttpService);
  private readonly sequenceService = inject(SequenceHttpService);
  private readonly mediaLocalPreviewService = inject(MediaLocalPreviewService);
  private readonly fileService = inject(VideoUploadFileService);
  private readonly translocoService = inject(TranslocoService);
  private readonly window = inject(WA_WINDOW);

  // Every client the user can upload long videos to. SUPPORT grants write access but is Pixacare
  // staff, never an upload owner — so it is excluded.
  loadUploadSessionsOnLicenses$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(licensesActions.loadLicensesSuccess),
      mergeMap(({ flattenedLicenses }) =>
        flattenedLicenses
          .filter(
            ({ client, license }) =>
              (client.isLongVideoEnabled && license.state === LicenseState.BASIC) ||
              license.state === LicenseState.ADMINISTRATOR,
          )
          .map(({ client }) => videoUploadsActions.loadUploadSessions({ clientCode: client.code })),
      ),
    ),
  );

  loadUploadSessions$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(videoUploadsActions.loadUploadSessions),
      mergeMap(({ clientCode }) =>
        this.videoUploadService.getUploadSessions(clientCode).pipe(
          map((sessions) => videoUploadsActions.loadUploadSessionsSuccess({ clientCode, sessions })),
          catchError((error: Error) =>
            of(videoUploadsActions.loadUploadSessionsError({ clientCode, error: error.message })),
          ),
        ),
      ),
    ),
  );

  // Interrupted sessions carry no patient identity, so fetch each hydrated sequence to fill its
  // sync-center title (patient name) and creation date — matching a first-time upload's display.
  loadInterruptedSequencesInfo$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(videoUploadsActions.loadUploadSessionsSuccess),
      mergeMap(({ clientCode, sessions }) => {
        const uniqueSequenceIds = [...new Set(sessions.map((session) => session.sequenceId))];
        return from(uniqueSequenceIds).pipe(
          mergeMap((sequenceId) =>
            this.sequenceService.getSequence(clientCode, sequenceId).pipe(
              map((sequence) =>
                videoUploadsActions.setSequenceUploadInfo({
                  clientCode,
                  sequenceId,
                  // Same name rendering as the patient card (LASTNAME Firstname), "Anonyme" when
                  // there is no patient — never the raw `fullName` (reversed order).
                  title: sequence.patientInstance
                    ? formatPatientName(sequence.patientInstance)
                    : this.translocoService.translate('Anonyme'),
                  createdOn: sequence.sequenceInstance.createdOn,
                }),
              ),
              catchError(() =>
                of(
                  videoUploadsActions.setSequenceUploadInfo({
                    clientCode,
                    sequenceId,
                    title: this.translocoService.translate('Séquence indisponible'),
                  }),
                ),
              ),
            ),
          ),
        );
      }),
    ),
  );

  // When the ongoing sessions are (re)loaded, re-insert a gray-square placeholder in the gallery for
  // every interrupted video — matching the reconcile done on sequence load, but the other way round:
  // this covers the case where the sequence is already in the store when the sessions come back. The
  // reducer no-ops for a sequence not loaded yet (that one is handled by reconcile on its next load).
  insertInterruptedMediaPlaceholders$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(videoUploadsActions.loadUploadSessionsSuccess),
      mergeMap(({ clientCode, sessions }) =>
        Object.entries(groupSessionsBySequence(sessions)).map(([sequenceId, sequenceSessions]) =>
          sequencesActions.insertOptimisticMedias({
            clientCode,
            sequenceId: Number(sequenceId),
            medias: sequenceSessions.map((session) =>
              toOptimisticMedia(
                clientCode,
                Number(sequenceId),
                session.originalFileName,
                session.fileSize,
                MediaType.VIDEO,
              ),
            ),
          }),
        ),
      ),
    ),
  );

  // Finished videos are attached to their sequence server-side, but the video store is disjoint from
  // sequence.medias — so refetch the sequence to replace the optimistic placeholders with the real
  // medias. Wait until the sequence has no video still running, otherwise the refetch would drop the
  // siblings still uploading (they aren't attached yet) from the carousel.
  // Only for the active client: the sequences slice is not client-scoped, so fetching another
  // client's sequence would list it here. Its own gallery reloads when the user switches to it.
  refetchSequenceOnVideoUploadSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(videoUploadsActions.videoUploadSuccess),
      withLatestFrom(this.store.select(selectActiveClientCode)),
      filter(([{ clientCode }, activeClientCode]) => clientCode === activeClientCode),
      mergeMap(([{ clientCode, sequenceId }]) =>
        this.store.select(selectSequenceHasWaitingOrInProgressVideos({ clientCode, sequenceId })).pipe(
          take(1),
          filter((hasActiveVideos) => !hasActiveVideos),
          map(() => sequencesActions.getUserSequence({ clientCode, sequenceId })),
        ),
      ),
    ),
  );

  // Videos skip updateDataConsumptionOnAttachSuccess$ (sequences.effects.ts): the real server Media
  // isn't known synchronously here, only the chunks being done — so this uses the known file size.
  updateDataConsumptionOnVideoUploadSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(videoUploadsActions.videoUploadSuccess),
      concatMap(({ clientCode, sequenceId, videoId }) =>
        this.store.select(selectVideoUploadEntry({ clientCode, sequenceId, videoId })).pipe(
          take(1),
          filter((entry): entry is NonNullable<typeof entry> => !!entry),
          map((entry) =>
            clientsActions.updateDataConsumption({
              clientCode,
              medias: [toOptimisticMedia(clientCode, sequenceId, entry.fileName, entry.fileSize, MediaType.VIDEO)],
            }),
          ),
        ),
      ),
    ),
  );

  // Retains each uploaded File so an errored upload can be retried from the same in-memory bytes.
  // A side effect on a non-store registry (the File is non-serializable), symmetric with the
  // release effect below — kept out of the upload orchestration so both live in one place.
  retainBatchUploadFiles$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(videoUploadsActions.uploadSequenceVideos),
        tap(({ videos }) => videos.forEach(({ videoId, file }) => this.fileService.retain(videoId, file))),
      ),
    { dispatch: false },
  );

  retainResumedUploadFile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(videoUploadsActions.resumeVideoUpload),
        tap(({ videoId, file }) => this.fileService.retain(videoId, file)),
      ),
    { dispatch: false },
  );

  // Drop the retained File handle once a video no longer needs a retry: it finished, or it left the
  // store. The File is the heavy resource, so it goes even on success (a succeeded upload is never
  // retried); the preview object URL is kept — see revokeVideoUploadPreview$.
  releaseVideoUploadFile$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(videoUploadsActions.removeVideoUpload, videoUploadsActions.videoUploadSuccess),
        tap(({ videoId }) => this.fileService.release(videoId)),
      ),
    { dispatch: false },
  );

  // Revoke the preview object URL only when a video leaves the store (delete). On success we keep it
  // so the sync-center still shows the video thumbnail — it is only released on a page refresh,
  // where object URLs don't survive and the store resets anyway.
  revokeVideoUploadPreview$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(videoUploadsActions.removeVideoUpload),
        tap(({ videoId }) => this.mediaLocalPreviewService.revoke(videoId)),
      ),
    { dispatch: false },
  );

  // Logout wipes the slice in one action, so no per-video release fires — drop everything held here.
  releaseAllVideoUploadResourcesOnReset$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(lifeCycleActions.resetState),
        tap(() => {
          this.mediaLocalPreviewService.clear();
          this.fileService.clear();
        }),
      ),
    { dispatch: false },
  );

  // Warns the user before they leave while a video upload is still running: the File bytes can't
  // survive a reload, so a lost tab means a lost upload. Runs regardless of which component is
  // mounted, unlike a service instantiated for its side effect from a specific component.
  guardUnloadWhileUploading$ = createEffect(
    () =>
      this.store.select(selectHasActiveVideoUploads).pipe(
        distinctUntilChanged(),
        tap((hasActiveUploads) => {
          if (hasActiveUploads) {
            this.window.addEventListener('beforeunload', this.preventUnload);
          } else {
            this.window.removeEventListener('beforeunload', this.preventUnload);
          }
        }),
      ),
    { dispatch: false },
  );

  // Triggers the browser's native "leave site?" confirmation. Modern browsers show the prompt on
  // preventDefault() alone (the old returnValue is deprecated).
  private readonly preventUnload = (event: BeforeUnloadEvent): void => {
    event.preventDefault();
  };

  // Best-effort server cleanup of the dead interrupted session; failures are swallowed since the
  // resume proceeds regardless and a stale session only means it lingers until the next attempt.
  abortInterruptedSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(videoUploadsActions.abortInterruptedSession),
        mergeMap(({ clientCode, sessionToken }) =>
          this.videoUploadService.abortUploadSession(clientCode, sessionToken).pipe(catchError(() => of(void 0))),
        ),
      ),
    { dispatch: false },
  );

  // Resumes one interrupted video: re-adds it optimistically to the sequence (it left the carousel
  // on reload) and uploads the re-picked File against its existing session (so only the chunks the
  // server misses are sent), mapping callbacks back to the existing entry's videoId.
  resumeVideoUpload$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(videoUploadsActions.resumeVideoUpload),
      concatMap((action) => this.runResumeVideoUpload(action)),
    ),
  );

  private runResumeVideoUpload({
    clientCode,
    sequenceId,
    videoId,
    file,
    sessionToken,
  }: ReturnType<typeof videoUploadsActions.resumeVideoUpload>): Observable<Action> {
    const request: VideoUploadItem = { file, videoId, sessionToken };
    // One-video call: the lib always reports it at position 0, which maps back to this video's id.
    return this.videoUploadService.uploadVideos(clientCode, sequenceId, [request]).pipe(
      map((event) => this.videoUploadEventToAction(clientCode, sequenceId, videoId, event)),
      startWith(
        sequencesActions.insertOptimisticMedias({
          clientCode,
          sequenceId,
          medias: [toOptimisticMedia(clientCode, sequenceId, file.name, file.size, MediaType.VIDEO)],
        }),
      ),
    );
  }

  uploadSequenceVideos$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(videoUploadsActions.uploadSequenceVideos),
      mergeMap((action) => this.runSequenceVideoUpload(action)),
    ),
  );

  private runSequenceVideoUpload({
    clientCode,
    sequenceId,
    videos,
  }: ReturnType<typeof videoUploadsActions.uploadSequenceVideos>): Observable<Action> {
    return this.videoUploadService
      .uploadVideos(
        clientCode,
        sequenceId,
        videos.map(({ file, videoId }) => ({ file, videoId })),
      )
      .pipe(
        map((event) => this.videoUploadEventToAction(clientCode, sequenceId, videos[event.videoIndex].videoId, event)),
      );
  }

  private videoUploadEventToAction(
    clientCode: string,
    sequenceId: number,
    videoId: string,
    event: VideoUploadEvent,
  ): Action {
    switch (event.type) {
      case 'progress':
        return videoUploadsActions.updateVideoUploadProgress({
          clientCode,
          sequenceId,
          videoId,
          syncedBytes: event.syncedBytes,
          remainingSeconds: event.remainingSeconds,
        });
      case 'success':
        return videoUploadsActions.videoUploadSuccess({ clientCode, sequenceId, videoId });
      case 'error':
        return videoUploadsActions.videoUploadError({
          clientCode,
          sequenceId,
          videoId,
          error: event.error,
          sessionToken: event.sessionToken,
        });
    }
  }

  // Each path that (re)loads sequences overwrites their medias with server truth (which excludes still-
  // uploading videos), so re-derive one placeholder per not-yet-succeeded video, deduplicated by content key.
  reconcileSequenceMediaPlaceholders$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.getSequenceSuccess, sequencesActions.updateFlattenedSequenceSuccess),
      concatMap(({ clientCode, sequence }) => this.reconcilePlaceholders(clientCode, [sequence.sequenceInstance.id])),
    ),
  );

  reconcileGalleryMediaPlaceholders$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.getNextUserSequencesSuccess, sequencesActions.getPreviousUserSequencesSuccess),
      concatMap(({ clientCode, collection }) =>
        this.reconcilePlaceholders(
          clientCode,
          collection.data.map((sequence) => sequence.sequenceInstance.id),
        ),
      ),
    ),
  );

  reconcileLoadedSequencesMediaPlaceholders$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(sequencesActions.loadSequences),
      concatMap(({ clientCode, data }) =>
        this.reconcilePlaceholders(
          clientCode,
          data.map((sequence) => sequence.sequenceInstance.id),
        ),
      ),
    ),
  );

  private reconcilePlaceholders(clientCode: string, sequenceIds: number[]): Observable<Action> {
    return this.store.select(selectVideoUploadsState).pipe(
      take(1),
      mergeMap((state) =>
        sequenceIds.flatMap((sequenceId) => this.sequencePlaceholderActions(state, clientCode, sequenceId)),
      ),
    );
  }

  private sequencePlaceholderActions(state: VideoUploadsState, clientCode: string, sequenceId: number): Action[] {
    const placeholders = Object.values(state[clientCode]?.[sequenceId]?.videos ?? {})
      .filter((video) => video.status !== ActionStatus.SUCCESS)
      .map((video) => toOptimisticMedia(clientCode, sequenceId, video.fileName, video.fileSize, MediaType.VIDEO));

    return placeholders.length > 0
      ? [sequencesActions.insertOptimisticMedias({ clientCode, sequenceId, medias: placeholders })]
      : [];
  }
}
